import React from "react";
import { CopyButton } from "components/common/CopyButton";

export interface CodeBlockProps {
  /**
   * The literal text rendered AND copied to the clipboard. Source of truth.
   */
  code: string;
  /** Required for screen readers. */
  ariaLabel: string;
  /**
   * Optional placeholder substring (e.g. `<paste your token>`). Each
   * occurrence in `code` is wrapped in a `text-warning` span when displayed,
   * while the clipboard copy keeps the literal text. Lets call sites stay
   * DRY (no duplicate JSX rebuilding the snippet).
   */
  placeholder?: string;
  /** Fired after a successful copy (e.g. for global toast notifications). */
  onCopy?: () => void;
  className?: string;
}

const renderHighlighted = (
  code: string,
  placeholder: string,
): React.ReactNode => {
  const parts = code.split(placeholder);
  return parts.flatMap((part, i) =>
    i < parts.length - 1
      ? [
          part,
          <span key={i} className="text-warning">
            {placeholder}
          </span>,
        ]
      : [part],
  );
};

export const CodeBlock: React.FC<CodeBlockProps> = ({
  code,
  ariaLabel,
  placeholder,
  onCopy,
  className = "",
}) => {
  const content = placeholder ? renderHighlighted(code, placeholder) : code;

  return (
    <div className={`relative border rounded-app bg-list-hover ${className}`}>
      <pre
        aria-label={ariaLabel}
        className="text-mono-small overflow-x-auto px-3 py-2"
        style={{ whiteSpace: "pre", margin: 0 }}
      >
        {content}
      </pre>
      {/* Floating, semi-transparent so the code below stays partially visible. */}
      <div className="absolute top-1 right-1">
        <CopyButton
          text={code}
          size="xs"
          ariaLabel={ariaLabel}
          onCopy={onCopy}
          className="bg-bg/60! hover:bg-bg/90! backdrop-blur-sm"
        />
      </div>
    </div>
  );
};
